import type {
    Customer,
    Invoice,
    InvoiceStatus,
    PaymentMethod
} from '@/types'
import type { Random } from '@/lib/random'

import { createRandom, hashString } from '@/lib/random'
import { TODAY, addDays, addMonths, daysBetween, getDayOfMonth } from '@/lib/date'
import { CUSTOMERS, FEATURED_CUSTOMER_IDS, isPaying } from '@/data/customers'

// Invoices issued in the last 30 days for every paying customer, numbered per month

const FORCED_STATUS: Record<string, InvoiceStatus> = {
    [FEATURED_CUSTOMER_IDS.majuBersama]: 'failed',
    [FEATURED_CUSTOMER_IDS.mitraTekstil]: 'pending',
    [FEATURED_CUSTOMER_IDS.klinikGigi]: 'paid',
    [FEATURED_CUSTOMER_IDS.kopiSenja]: 'paid'
}

const compare = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

const pad = (value: number): string => String(value).padStart(2, '0')

const billingDay = (customer: Customer): number => Math.min(getDayOfMonth(customer.joinedAt), 28)

// Most recent monthly billing date on or before the reference date
const latestMonthlyDate = (customer: Customer, reference: string): string => {
    const candidate = `${reference.slice(0, 7)}-${pad(billingDay(customer))}`
    return candidate > reference ? addMonths(candidate, -1) : candidate
}

// Most recent yearly renewal date on or before the reference date
const latestAnnualDate = (customer: Customer, reference: string): string => {
    const candidate = `${reference.slice(0, 4)}-${customer.joinedAt.slice(5, 7)}-${pad(billingDay(customer))}`
    return candidate > reference ? addMonths(candidate, -12) : candidate
}

export const latestBillingDate = (customer: Customer, reference: string = TODAY): string => (
    customer.cycle === 'annual' ? latestAnnualDate(customer, reference) : latestMonthlyDate(customer, reference)
)

export const nextBillingDate = (customer: Customer): string => addMonths(latestBillingDate(customer), customer.cycle === 'annual' ? 12 : 1)

const pickStatus = (customer: Customer, issuedAt: string, random: Random): InvoiceStatus => {
    const forced = FORCED_STATUS[customer.id]
    if (forced) return forced

    const age = daysBetween(issuedAt, TODAY)

    if (customer.status === 'at-risk') return random.weighted<InvoiceStatus>([['failed', 40], ['pending', 15], ['paid', 45]])
    if (age <= 1) return random.chance(0.55) ? 'pending' : 'paid'

    return random.weighted<InvoiceStatus>([['paid', 95], ['failed', 2], ['pending', 2], ['refunded', 1]])
}

const invoiceAmount = (customer: Customer): number => (customer.cycle === 'annual' ? customer.mrr * 12 : customer.mrr)

const createInvoices = (): Invoice[] => {
    const random = createRandom(5150)
    const drafts: Omit<Invoice, 'number'>[] = []

    CUSTOMERS.forEach((customer) => {
        if (!isPaying(customer)) return

        const issuedAt = latestBillingDate(customer)
        if (daysBetween(issuedAt, TODAY) > 30 || issuedAt < customer.joinedAt) return

        const status = pickStatus(customer, issuedAt, random)
        const paidOffset = Math.min(daysBetween(issuedAt, TODAY), random.int(0, 2))

        drafts.push({
            id: `inv-${customer.id}-${issuedAt}`,
            customerId: customer.id,
            company: customer.company,
            contact: customer.contact,
            email: customer.email,
            city: customer.city,
            plan: customer.plan,
            cycle: customer.cycle,
            seats: customer.seats,
            amount: invoiceAmount(customer),
            status,
            method: customer.paymentMethod,
            issuedAt,
            dueAt: addDays(issuedAt, 7),
            paidAt: status === 'paid' || status === 'refunded' ? addDays(issuedAt, paidOffset) : null
        })
    })

    drafts.sort((a, b) => compare(a.issuedAt, b.issuedAt) || compare(a.customerId, b.customerId))

    const counters = new Map<string, number>()

    const numbered = drafts.map((draft) => {
        const period = draft.issuedAt.slice(0, 7).replace('-', '')
        const sequence = (counters.get(period) ?? 0) + 1
        counters.set(period, sequence)

        return { ...draft, number: `INV-${period}-${String(sequence).padStart(4, '0')}` }
    })

    return numbered.reverse()
}

export const INVOICES: Invoice[] = createInvoices()

const INVOICE_BY_CUSTOMER = new Map(INVOICES.map((invoice) => [invoice.customerId, invoice]))

export const isOverdue = (invoice: Pick<Invoice, 'status' | 'dueAt'>): boolean => (
    (invoice.status === 'pending' || invoice.status === 'failed') && invoice.dueAt < TODAY
)

export const getInvoiceStats = (list: Invoice[]) => {
    const sum = (status?: InvoiceStatus) => list
        .filter((invoice) => !status || invoice.status === status)
        .reduce((total, invoice) => total + invoice.amount, 0)
    const count = (status: InvoiceStatus) => list.filter((invoice) => invoice.status === status).length

    return {
        billed: sum(),
        paid: sum('paid'),
        pending: sum('pending'),
        failed: sum('failed'),
        refunded: sum('refunded'),
        counts: {
            all: list.length,
            paid: count('paid'),
            pending: count('pending'),
            failed: count('failed'),
            refunded: count('refunded')
        }
    }
}

export const getPaymentMethodBreakdown = (list: Invoice[]): { method: PaymentMethod, amount: number, count: number }[] => {
    const totals = new Map<PaymentMethod, { amount: number, count: number }>()

    list.filter((invoice) => invoice.status === 'paid').forEach((invoice) => {
        const current = totals.get(invoice.method) ?? { amount: 0, count: 0 }
        totals.set(invoice.method, { amount: current.amount + invoice.amount, count: current.count + 1 })
    })

    return Array.from(totals.entries())
        .map(([method, value]) => ({ method, ...value }))
        .sort((a, b) => b.amount - a.amount)
}

// Billing history for one customer, used by the customer detail panel
export const getCustomerInvoices = (customer: Customer): Invoice[] => {
    if (customer.status === 'trial') return []

    const random = createRandom(hashString(`history-${customer.id}`))
    const reference = customer.status === 'canceled' && customer.canceledAt ? customer.canceledAt : TODAY
    const step = customer.cycle === 'annual' ? 12 : 1
    const current = INVOICE_BY_CUSTOMER.get(customer.id)
    const history: Invoice[] = []
    let issuedAt = latestBillingDate(customer, reference)

    for (let position = 0; position < 6 && issuedAt >= customer.joinedAt; position++) {
        if (position === 0 && current && current.issuedAt === issuedAt) {
            history.push(current)
        }
        else {
            const period = issuedAt.slice(0, 7).replace('-', '')
            const status: InvoiceStatus = customer.status === 'at-risk' && position === 1 && random.chance(0.5) ? 'failed' : 'paid'

            history.push({
                id: `inv-${customer.id}-${issuedAt}`,
                number: `INV-${period}-${String((hashString(`${customer.id}${issuedAt}`) % 9000) + 1000)}`,
                customerId: customer.id,
                company: customer.company,
                contact: customer.contact,
                email: customer.email,
                city: customer.city,
                plan: customer.plan,
                cycle: customer.cycle,
                seats: customer.seats,
                amount: invoiceAmount(customer),
                status,
                method: customer.paymentMethod,
                issuedAt,
                dueAt: addDays(issuedAt, 7),
                paidAt: status === 'paid' ? addDays(issuedAt, random.int(0, 2)) : null
            })
        }

        issuedAt = addMonths(issuedAt, -step)
    }

    return history
}
